import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  UserPlus, 
  UserCheck, 
  UserX, 
  Mail, 
  Clock, 
  CheckCircle, 
  XCircle,
  Trash2,
  AlertCircle
} from 'lucide-react';
import { CreditApprover, UserProfile } from '../types/user';
import { userService } from '../services/userService';
import { useNotification } from '../contexts/NotificationContext';

const Container = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
  border: 1px solid #f1f5f9;
`;

const Title = styled.h3`
  margin: 0 0 32px 0;
  font-size: 28px;
  font-weight: 800;
  background: linear-gradient(135deg, #F57C00 0%, #E65100 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.5px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h4`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AddApproverForm = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 24px;
  padding: 24px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #F57C00;
    box-shadow: 0 0 0 4px rgba(245, 124, 0, 0.1);
  }
`;

const Button = styled(motion.button)<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  ${(props: { variant?: 'primary' | 'secondary' | 'danger' }) => {
    switch (props.variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #F57C00 0%, #E65100 100%);
          color: white;
          &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(245, 124, 0, 0.3); }
        `;
      case 'secondary':
        return `
          background: #64748b;
          color: white;
          &:hover { background: #475569; transform: translateY(-1px); }
        `;
      case 'danger':
        return `
          background: #ef4444;
          color: white;
          &:hover { background: #dc2626; transform: translateY(-1px); }
        `;
      default:
        return `
          background: #f1f5f9;
          color: #374151;
          border: 1px solid #e2e8f0;
          &:hover { background: #e2e8f0; }
        `;
    }
  }}
`;

const ApproverList = styled.div`
  display: grid;
  gap: 16px;
`;

const ApproverCard = styled(motion.div)<{ status: 'pending' | 'accepted' | 'rejected' }>`
  padding: 20px;
  border-radius: 12px;
  border: 2px solid ${(props: { status: 'pending' | 'accepted' | 'rejected' }) => {
    switch (props.status) {
      case 'accepted': return '#10b981';
      case 'rejected': return '#ef4444';
      default: return '#f59e0b';
    }
  }};
  background: ${(props: { status: 'pending' | 'accepted' | 'rejected' }) => {
    switch (props.status) {
      case 'accepted': return '#f0fdf4';
      case 'rejected': return '#fef2f2';
      default: return '#fffbeb';
    }
  }};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ApproverInfo = styled.div`
  flex: 1;
`;

const ApproverEmail = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
`;

const ApproverStatus = styled.div<{ status: 'pending' | 'accepted' | 'rejected' }>`
  font-size: 14px;
  font-weight: 500;
  color: ${(props: { status: 'pending' | 'accepted' | 'rejected' }) => {
    switch (props.status) {
      case 'accepted': return '#059669';
      case 'rejected': return '#dc2626';
      default: return '#d97706';
    }
  }};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ApproverDate = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
`;

const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #64748b;
`;

const AlertBanner = styled.div`
  padding: 16px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #92400e;
`;

interface UserManagementProps {
  onClose: () => void;
}

const UserManagement: React.FC<UserManagementProps> = ({ onClose }) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [approvers, setApprovers] = useState<CreditApprover[]>([]);
  const [usersIApprove, setUsersIApprove] = useState<{ userId: string; displayName: string; email: string; photoURL?: string }[]>([]);
  const [newApproverEmail, setNewApproverEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { showSuccess, showError, showWarning } = useNotification();

  useEffect(() => {
    loadUserData();
    loadUsersIApprove();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const [profile, approversList] = await Promise.all([
        userService.getUserProfile(),
        userService.getAllCreditApprovers()
      ]);
      setUserProfile(profile);
      setApprovers(approversList);
    } catch (error) {
      console.error('Error loading user data:', error);
      showError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const loadUsersIApprove = async () => {
    try {
      const users = await userService.getUsersIApprove();
      setUsersIApprove(users);
    } catch (error) {
      setUsersIApprove([]);
    }
  };

  const handleAddApprover = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!newApproverEmail.trim()) {
      showError('Please enter an email address');
      return;
    }

    if (!newApproverEmail.includes('@')) {
      showError('Please enter a valid email address');
      return;
    }

    try {
      setSubmitting(true);
      await userService.requestCreditApprover(newApproverEmail.trim());
      showSuccess('Credit approver request sent successfully!');
      setNewApproverEmail('');
      await loadUserData(); // Reload data
    } catch (error) {
      console.error('Error adding approver:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add approver';
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveApprover = async (approverId: string) => {
    try {
      setSubmitting(true);
      await userService.removeCreditApprover(approverId);
      showSuccess('Credit approver removed successfully!');
      await loadUserData(); // Reload data
    } catch (error) {
      console.error('Error removing approver:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to remove approver';
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status: 'pending' | 'accepted' | 'rejected') => {
    switch (status) {
      case 'accepted':
        return <CheckCircle size={16} />;
      case 'rejected':
        return <XCircle size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  const getStatusText = (status: 'pending' | 'accepted' | 'rejected') => {
    switch (status) {
      case 'accepted':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      default:
        return 'Pending';
    }
  };

  if (loading) {
    return (
      <Container>
        <div>Loading user management...</div>
      </Container>
    );
  }

  return (
    <Container
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <Title>
        <Users />
        User Management
      </Title>

      {userProfile && !userProfile.canCreateCreditedTasks && (
        <AlertBanner>
          <AlertCircle size={20} />
          <div>
            <strong>Credit Approval Required</strong>
            <br />
            You need at least one approved credit approver to create tasks with credits.
          </div>
        </AlertBanner>
      )}

      <Section>
        <SectionTitle>
          <UserPlus />
          Add Credit Approver
        </SectionTitle>
        
        <form onSubmit={handleAddApprover}>
          <AddApproverForm>
            <FormGroup>
              <Label htmlFor="approverEmail">Approver Email</Label>
              <Input
                id="approverEmail"
                type="email"
                value={newApproverEmail}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewApproverEmail(e.target.value)}
                placeholder="Enter approver's email address"
                required
              />
            </FormGroup>
            
            <Button
              type="submit"
              variant="primary"
              disabled={submitting || !newApproverEmail.trim()}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Mail />
              {submitting ? 'Sending...' : 'Send Request'}
            </Button>
          </AddApproverForm>
        </form>
      </Section>

      <Section>
        <SectionTitle>
          <UserCheck />
          Credit Approver Requests ({approvers.length})
        </SectionTitle>
        
        {approvers.length === 0 ? (
          <EmptyState>
            <UserX size={48} />
            <h4>No Credit Approver Requests</h4>
            <p>Add credit approvers to enable credited task creation.</p>
          </EmptyState>
        ) : (
          <ApproverList>
            <AnimatePresence>
              {approvers.map((approver) => (
                <ApproverCard
                  key={approver.id}
                  status={approver.status}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <ApproverInfo>
                    <ApproverEmail>{approver.approverEmail}</ApproverEmail>
                    <ApproverStatus status={approver.status}>
                      {getStatusIcon(approver.status)}
                      {getStatusText(approver.status)}
                    </ApproverStatus>
                    <ApproverDate>
                      Requested: {approver.requestedAt.toLocaleDateString()}
                      {approver.respondedAt && (
                        <span style={{ marginLeft: '12px', color: '#64748b' }}>
                          • Responded: {approver.respondedAt.toLocaleDateString()}
                        </span>
                      )}
                    </ApproverDate>
                    {approver.responseMessage && (
                      <div style={{ marginTop: '8px', fontSize: '13px', color: '#64748b', fontStyle: 'italic' }}>
                        "{approver.responseMessage}"
                      </div>
                    )}
                  </ApproverInfo>
                  <StatusIcon>
                    {approver.status === 'pending' && (
                      <Button
                        variant="danger"
                        onClick={async () => {
                          if (window.confirm('Cancel this pending request?')) {
                            await userService.removeCreditApproverByApproverUid(approver.id);
                            showSuccess('Request cancelled successfully!');
                            await loadUserData();
                          }
                        }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Trash2 />
                        Cancel
                      </Button>
                    )}
                    {approver.status === 'accepted' && (
                      <Button
                        variant="danger"
                        onClick={async () => {
                          if (window.confirm('Remove this credit approver?')) {
                            await userService.removeCreditApproverByApproverUid(approver.id);
                            showSuccess('Credit approver removed successfully!');
                            await loadUserData();
                          }
                        }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Trash2 />
                        Remove
                      </Button>
                    )}
                  </StatusIcon>
                </ApproverCard>
              ))}
            </AnimatePresence>
          </ApproverList>
        )}
      </Section>

      {usersIApprove.length > 0 && (
        <Section>
          <SectionTitle>
            <UserCheck /> Users You Can Approve Credit For
          </SectionTitle>
          <ul style={{ paddingLeft: 0, listStyle: 'none' }}>
            {usersIApprove.map(user => (
              <li key={user.userId} style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                {user.photoURL ? (
                  <img src={user.photoURL} alt={user.displayName} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #e2e8f0' }} />
                ) : (
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#64748b', fontSize: 16, border: '1.5px solid #e2e8f0' }}>
                    {user.displayName?.[0] || '?'}
                  </div>
                )}
                <div>
                  <div style={{ fontWeight: 600 }}>{user.displayName}</div>
                  <div style={{ color: '#64748b', fontSize: 13 }}>{user.email}</div>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '32px' }}>
        <Button
          variant="secondary"
          onClick={onClose}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Close
        </Button>
      </div>
    </Container>
  );
};

export default UserManagement; 